import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../../utils/api';
import FeatureTip from '../../components/FeatureTip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, KeyRound, Wrench } from 'lucide-react';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);      // Sätt = employee-doc för att redigera
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', role: '', hourly_rate: '',
    fixed_monthly_salary: '', pin_code: '',
    salary_mode: 'hourly',
    // Löneuppgifter för svensk payroll
    personnummer: '', street_address: '', postal_code: '', city: '',
    bank_account: '', is_consultant: false, f_skatt_number: '',
    has_pension_plan: false,
  });

  useEffect(() => { fetchEmployees(); }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.getEmployees();
      setEmployees(response.data || []);
    } catch (err) { toast.error('Kunde inte hämta personal'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingEmp(null);
    setFormData({
      name: '', phone: '', email: '', role: '', hourly_rate: '',
      fixed_monthly_salary: '', pin_code: '', salary_mode: 'hourly',
      personnummer: '', street_address: '', postal_code: '', city: '',
      bank_account: '', is_consultant: false, f_skatt_number: '',
      has_pension_plan: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditingEmp(emp);
    setFormData({
      name: emp.name || '',
      phone: emp.phone || '',
      email: emp.email || '',
      role: emp.role_title || '',
      hourly_rate: emp.hourly_rate != null ? String(emp.hourly_rate) : '',
      fixed_monthly_salary: emp.fixed_monthly_salary != null ? String(emp.fixed_monthly_salary) : '',
      pin_code: emp.pin_code || '',
      salary_mode: emp.fixed_monthly_salary ? 'fixed' : 'hourly',
      personnummer: emp.personnummer || '',
      street_address: emp.street_address || '',
      postal_code: emp.postal_code || '',
      city: emp.city || '',
      bank_account: emp.bank_account || '',
      is_consultant: !!emp.is_consultant,
      f_skatt_number: emp.f_skatt_number || '',
      has_pension_plan: !!emp.has_pension_plan,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Ange namn'); return; }
    // Om pin är satt, validera 4 siffror
    if (formData.pin_code && !/^\d{4}$/.test(formData.pin_code)) {
      toast.error('Pinkod måste vara exakt 4 siffror'); return;
    }
    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email && formData.email.trim() ? formData.email.trim() : null,
      role: formData.role || null,
      hourly_rate: formData.salary_mode === 'hourly' && formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      fixed_monthly_salary: formData.salary_mode === 'fixed' && formData.fixed_monthly_salary ? parseFloat(formData.fixed_monthly_salary) : null,
      pin_code: formData.pin_code || null,
      // Löneuppgifter (svensk payroll)
      personnummer: formData.personnummer || null,
      street_address: formData.street_address || null,
      postal_code: formData.postal_code || null,
      city: formData.city || null,
      bank_account: formData.bank_account ? formData.bank_account.replace(/\s/g, '').toUpperCase() : null,
      is_consultant: !!formData.is_consultant,
      f_skatt_number: formData.f_skatt_number || null,
      has_pension_plan: !!formData.has_pension_plan,
    };
    try {
      if (editingEmp) {
        await api.updateEmployee(editingEmp.id, payload);
        toast.success('Personal uppdaterad');
      } else {
        await api.createEmployee(payload);
        toast.success(payload.email ? 'Personal tillagd! (Lösenord: operio2026)' : 'Personal tillagd med internt konto');
      }
      setDialogOpen(false);
      setEditingEmp(null);
      fetchEmployees();
    } catch (err) {
      toast.error(formatApiError(err, 'Kunde inte spara personal'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Är du säker? Detta tar bort användarkontot.')) return;
    try {
      await api.deleteEmployee(id);
      toast.success('Personal borttagen');
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort personal');
    }
  };

  const handleSetPin = async () => {
    if (pinValue && (pinValue.length !== 4 || !/^\d{4}$/.test(pinValue))) {
      toast.error('Pinkod måste vara exakt 4 siffror'); return;
    }
    try {
      await api.setEmployeePin(selectedEmp.id, { pin_code: pinValue });
      toast.success(pinValue ? 'Pinkod inställd!' : 'Pinkod borttagen');
      setPinDialogOpen(false);
      setPinValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ställa in pinkod');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="employees-page">
      <FeatureTip tipId="employees" title="Personalregister" body="Lägg till anställda med timlön och roll. När de stämplar in/ut räknas lön och arbetsgivaravgifter ut automatiskt — du behöver bara godkänna lönerna i Löner-fliken." />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Personal</h2>
          <p className="text-sm text-muted-foreground mt-1">Alla anställda har ett inloggningskonto</p>
        </div>
        <Button onClick={openCreate} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="new-employee-btn">
          <Plus size={14} className="mr-1.5" /> Ny personal
        </Button>
      </div>

      <div className="bg-card border border-border rounded">
        {employees.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm" data-testid="no-employees">Ingen personal tillagd</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Namn</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Roll</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Konto</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Telefon</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">E-post</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timtaxa</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Pinkod</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Åtgärd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {employees.map((emp) => (
                  <tr key={emp.id} data-testid={`employee-row-${emp.id}`}>
                    <td className="px-5 py-3 text-sm font-medium">{emp.name || '—'}</td>
                    <td className="px-5 py-3 text-sm">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        emp.role === 'admin' ? 'bg-blue-50 text-blue-700' :
                        emp.role === 'superadmin' ? 'bg-purple-50 text-purple-700' :
                        'bg-green-50 text-green-700'
                      }`}>{emp.role_title || emp.role}</span>
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{emp.role}</td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{emp.phone || '—'}</td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{emp.email}</td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">{emp.hourly_rate ? `${emp.hourly_rate} kr/h` : (emp.fixed_monthly_salary ? `${emp.fixed_monthly_salary} kr/mån` : '—')}</td>
                    <td className="px-5 py-3 text-sm">
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedEmp(emp); setPinValue(''); setPinDialogOpen(true); }}
                        data-testid={`set-pin-${emp.id}`} className="text-muted-foreground hover:text-foreground">
                        <KeyRound size={14} className="mr-1" /> {emp.pin_code ? 'Ändra' : 'Ställ in'}
                      </Button>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(emp)} className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950" data-testid={`edit-employee-${emp.id}`} title="Redigera">
                          <Wrench size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(emp.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-employee-${emp.id}`} title="Ta bort">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingEmp(null); }}>
        <DialogContent data-testid="create-employee-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{editingEmp ? `Redigera ${editingEmp.name}` : 'Ny personal'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Namn *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="employee-name-input" /></div>
            <div>
              <Label>E-post {editingEmp ? '' : '(valfritt — auto-genereras om tom)'}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={editingEmp ? '' : 'Lämna tom för att skapa internt konto'}
                data-testid="employee-email-input"
              />
              {!editingEmp && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Utan e-post skapas ett internt konto — arbetaren stämplar med PIN nedan.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="employee-phone-input" /></div>
              <div><Label>Roll/titel</Label><Input value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="Förare, Maskinist" data-testid="employee-role-input" /></div>
            </div>

            {/* Lönetyp — timtaxa eller fast lön */}
            <div>
              <Label>Lönetyp</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, salary_mode: 'hourly' })}
                  data-testid="employee-salary-hourly-btn"
                  className={`px-3 py-2 rounded-md border text-sm text-left transition-colors ${
                    formData.salary_mode === 'hourly'
                      ? 'border-[hsl(16,100%,55%)] bg-[hsl(16,100%,55%)]/10 text-foreground font-medium'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  Timtaxa (kr/h)
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, salary_mode: 'fixed' })}
                  data-testid="employee-salary-fixed-btn"
                  className={`px-3 py-2 rounded-md border text-sm text-left transition-colors ${
                    formData.salary_mode === 'fixed'
                      ? 'border-[hsl(16,100%,55%)] bg-[hsl(16,100%,55%)]/10 text-foreground font-medium'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  Fast månadslön
                </button>
              </div>
              {formData.salary_mode === 'hourly' ? (
                <Input
                  type="number" step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  placeholder="Ex. 350"
                  className="mt-2"
                  data-testid="employee-rate-input"
                />
              ) : (
                <Input
                  type="number" step="1"
                  value={formData.fixed_monthly_salary}
                  onChange={(e) => setFormData({ ...formData, fixed_monthly_salary: e.target.value })}
                  placeholder="Ex. 32000"
                  className="mt-2"
                  data-testid="employee-fixed-salary-input"
                />
              )}
            </div>

            <div>
              <Label>Pinkod för stämpling (valfritt — 4 siffror)</Label>
              <Input
                type="text" maxLength={4} inputMode="numeric"
                value={formData.pin_code}
                onChange={(e) => setFormData({ ...formData, pin_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder="0000"
                className="text-center font-mono tracking-[0.3em]"
                data-testid="employee-pin-input"
              />
            </div>

            {/* SVENSK PAYROLL — löneuppgifter */}
            <div className="border-t border-border pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-[#E8613A] font-bold">Löneuppgifter (för utbetalning)</p>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="is-consultant-toggle-wrap">
                  <input
                    type="checkbox"
                    checked={formData.is_consultant}
                    onChange={e => setFormData({ ...formData, is_consultant: e.target.checked })}
                    className="w-4 h-4 accent-[#E8613A]"
                    data-testid="is-consultant-toggle"
                  />
                  <span className="font-semibold">Konsult (F-skatt)</span>
                </label>
              </div>

              {formData.is_consultant ? (
                <div>
                  <Label className="text-xs">F-skattenummer / org.nr</Label>
                  <Input
                    value={formData.f_skatt_number}
                    onChange={e => setFormData({ ...formData, f_skatt_number: e.target.value })}
                    placeholder="556789-0123"
                    data-testid="f-skatt-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Konsulten skattar själv. Ingen A-skatt eller AG-avgift dras. Personnummer behövs inte.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Personnummer *</Label>
                    <Input
                      value={formData.personnummer}
                      onChange={e => setFormData({ ...formData, personnummer: e.target.value })}
                      placeholder="19900515-0009"
                      data-testid="personnummer-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Krävs för korrekt AG-avgift (ålderssats) + Skatteverkets AGI-rapport.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Label className="text-xs">Gatuadress</Label>
                      <Input
                        value={formData.street_address}
                        onChange={e => setFormData({ ...formData, street_address: e.target.value })}
                        placeholder="Vasagatan 12"
                        data-testid="street-address-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Postnr *</Label>
                      <Input
                        value={formData.postal_code}
                        onChange={e => setFormData({ ...formData, postal_code: e.target.value })}
                        placeholder="541 30"
                        data-testid="postal-code-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Ort</Label>
                    <Input
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Skövde"
                      data-testid="city-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Kommunen fylls i automatiskt från postnumret → styr rätt kommunalskatt.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_pension_plan}
                      onChange={e => setFormData({ ...formData, has_pension_plan: e.target.checked })}
                      className="w-4 h-4 accent-[#E8613A]"
                      data-testid="pension-toggle"
                    />
                    <span>Tjänstepension aktiverad (ITP1 — 4,5% under 7,5 IBB / 30% över)</span>
                  </label>
                </div>
              )}

              <div className="mt-3">
                <Label className="text-xs">Bankkonto IBAN {!formData.is_consultant && '*'}</Label>
                <Input
                  value={formData.bank_account}
                  onChange={e => setFormData({ ...formData, bank_account: e.target.value })}
                  placeholder="SE45 5000 0000 0583 9825 7466"
                  className="font-mono text-sm"
                  data-testid="iban-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Krävs för SEPA-utbetalning. Format: SE + 22 siffror (24 tecken totalt).
                </p>
              </div>
            </div>

            {!editingEmp && (
              <p className="text-xs text-muted-foreground">Standardlösenord för inloggning: <strong>operio2026</strong></p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
              <Button type="submit" className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="create-employee-submit-btn">
                {editingEmp ? 'Spara ändringar' : 'Lägg till'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="pin-dialog">
          <DialogHeader><DialogTitle>Pinkod — {selectedEmp?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Ange en 4-siffrig pinkod som krävs vid stämpling. Lämna tomt för att ta bort.</p>
            <Input
              type="text" maxLength={4} pattern="\d{0,4}" inputMode="numeric"
              value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000" className="text-center text-2xl tracking-[0.5em] font-mono h-14"
              data-testid="pin-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleSetPin} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="save-pin-btn">
              {pinValue ? 'Spara pinkod' : 'Ta bort pinkod'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
